<script setup>

import {computed, onMounted, reactive, ref, watch} from "vue";
import { useNotificationStore } from '@/stores/NotificationStore.js'
const notificationStore = useNotificationStore()

const emit = defineEmits('created')

const notificationContent = reactive({
    title: '',
    body: '',
    tags: [],
    datetime: new Date()
})
const state = ref('')
const tagSelector = ref()
const deliveryDateHasChanged = ref(false)
const deliveryValidates = ref(false)

const onDateSelected = (e) => {
    notificationContent.datetime = e;
    validateDeliveryDate()
}

const onTagLeadTagsSelected = (e) => {
    notificationContent.tags = e.map(tag => tag.id);
}

const notificationValidates = computed(() => {
    return notificationContent.title && notificationContent.body && notificationContent.datetime && notificationContent.tags.length > 0 && deliveryValidates.value;
})

const createNotification = async () => {
    if (!notificationValidates.value) {
        return;
    }

    state.value = 'waiting'

    const res = await axios.post(route('api.manage-whitelabel-app.store-notification'), notificationContent)

    if (res.data.success) {
        state.value = 'success'
    } else {
        state.value = 'error'
    }
    emit('created')
    resetNotificationContent();

    notificationStore.addNotification({
        type: 'success',
        message: _mft('whitelabelApp:notification.created.confirmation.title'),
        dismissAfter: 5000,
    })

    window.scrollTo(0, 0);
}

const resetNotificationContent = () => {
    notificationContent.title = '';
    notificationContent.body = '';
    notificationContent.tags = [];
    notificationContent.datetime = new Date();

    tagSelector.value.clearSelectedTags()
}

const validateDeliveryDate = () => {
    deliveryValidates.value = notificationContent.datetime &&
        notificationContent.datetime.getTime() > (new Date().getTime() + 15 * 60 * 1000);
}

const contentValidates = computed(() => {
    return notificationContent.title &&
        notificationContent.title.length <= 40 &&
        notificationContent.body &&
        notificationContent.body.length <= 120;
})

const recipientsValidates = computed(() => {
    return notificationContent.tags.length > 0;
})

setInterval(() => {
    validateDeliveryDate();
}, 60000);

watch(notificationContent,  () => {
    deliveryDateHasChanged.value = true
}, {deep: true})

onMounted(() => {
    const date = new Date();
    date.setHours(date.getHours() + 1);
    date.setMinutes(15);
    date.setSeconds(0);
    date.setMilliseconds(0);
    notificationContent.datetime = date;
    validateDeliveryDate()
})

</script>
<template>
    <div>
        <div class="mb-8">
            <div class="grid grid-cols-3 gap-8">
                <card-tabbed class="col-span-2">
                    <card-tabbed-tab
                        :value="'content'"
                        :title="'Content'"
                        :active="true"
                        :icon="contentValidates ? 'fa-regular fa-circle-check text-myflowGreen' : 'fa-solid fa-exclamation-triangle text-myflowRed'"
                    >
                        <h2>Content</h2>
                        <div>
                            <div class="">
                                <input-text
                                    :label="_mft('whitelabelApp:notification.title.label')"
                                    maxlength="40"
                                    v-model:content="notificationContent.title"
                                ></input-text>

                                <input-textarea
                                    :label="_mft('whitelabelApp:notification.body.label')"
                                    :maxlength="120"
                                    v-model:content="notificationContent.body"
                                ></input-textarea>

                                <p
                                    v-if="! contentValidates"
                                    class="text-center mt-2 text-xs text-red-600"
                                >
                                    Title and body are required. Title must be less than 40 characters, body less than 80 characters.
                                </p>
                            </div>
                        </div>
                    </card-tabbed-tab>
                    <card-tabbed-tab
                        :value="'recipients'"
                        :title="'Recipients'"
                        :icon="recipientsValidates ? 'fa-regular fa-circle-check text-myflowGreen' : 'fa-solid fa-exclamation-triangle text-myflowRed'"
                    >
                        <h2>Recipients</h2>
                        <tags-multi-select-v2
                            ref="tagSelector"
                            @tagsSelected="onTagLeadTagsSelected"
                            :placeholder="_mft('whitelabelApp:notification.tags.search')"
                            :allow-creation="true"
                            :select-multiple="true"
                        ></tags-multi-select-v2>

                        <p
                            v-if="! recipientsValidates"
                            class="text-center mt-2 text-xs text-red-600"
                        >
                            Select at least one tag to send the notification to.
                        </p>

                    </card-tabbed-tab>
                    <card-tabbed-tab
                        :value="'delivery'"
                        :title="'Delivery'"
                        :icon="deliveryValidates ? 'fa-regular fa-circle-check text-myflowGreen' : 'fa-solid fa-exclamation-triangle text-myflowRed'"
                    >
                        <input-datepicker
                            class="justify-center"
                            :date="notificationContent.datetime"
                            :minutes-increment="15"
                            :show-time="true"
                            @update:date="onDateSelected"
                        ></input-datepicker>

                        <p
                            v-if="! deliveryValidates"
                            class="text-center mt-2 text-xs text-red-600"
                        >
                            Delivery must be at least 15 minutes in the future.
                        </p>

                    </card-tabbed-tab>
                </card-tabbed>
                <div>
                    <div class="max-w-72 mx-auto">
                        <div class="flex justify-center transition-all" :class="notificationContent.title || notificationContent.body ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-12'">
                            <div class="bg-white rounded-lg shadow-lg w-full max-w-xs p-2 flex space-x-2 items-center">
                                <div>
                                    <div class="text-xs font-bold">{{ notificationContent.title }}</div>
                                    <div class="text-xs">{{ notificationContent.body }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="notificationContent.title || notificationContent.body"
                    >
                        <p class="text-xxs text-gray-500 text-center m-2">
                            This is just an illustration, appearance will differ from device to device.
                        </p>

                        <div class="mt-8">
                            <div class="flex justify-center">
                                <button-success
                                    :disabled="! notificationValidates"
                                    @click="createNotification"
                                >
                                    {{ _mft('whitelabelApp:notification.create.label') }}
                                </button-success>

                            </div>
                            <p
                                v-if="! notificationValidates"
                                class="text-xxs text-gray-500 text-center m-2"
                            >
                                Add content, recipients and delivery date to schedule a notification.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
